import React, { useState } from "react";
import useSWR from "swr";
import api, { API_BASE } from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Gavel, CheckCircle2, XCircle, AlertTriangle, Ban, Send, Clock, FileSignature, History as HistoryIcon, Camera, Pencil,
} from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATE_META = {
  pending_review: { label: "Pending review",  colour: "bg-amber-100 text-amber-800 border-amber-200",  icon: Clock },
  disputed:       { label: "Disputed",        colour: "bg-purple-100 text-purple-800 border-purple-200", icon: FileSignature },
  approved:       { label: "Approved",        colour: "bg-blue-100 text-blue-800 border-blue-200",    icon: CheckCircle2 },
  applied:        { label: "Applied",         colour: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  rejected:       { label: "Rejected",        colour: "bg-gray-100 text-gray-600 border-gray-200",    icon: XCircle },
  cancelled:      { label: "Cancelled",       colour: "bg-gray-100 text-gray-500 border-gray-200",    icon: Ban },
};

const StateBadge = ({ state }) => {
  const m = STATE_META[state] || STATE_META.pending_review;
  const I = m.icon;
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${m.colour}`}
           data-testid={`penalty-state-${state}`}>
      <I className="w-3 h-3 mr-1" /> {m.label}
    </Badge>
  );
};

const gbp = (n) => (typeof n === "number" ? `£${n.toFixed(2)}` : "—");

const EvidenceCaseDialog = ({ drivers, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ driver_id: "", penalty_type: "signature_dispute", reason: "", incident_date: "", file: null });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.driver_id || !form.file || form.reason.trim().length < 3) return toast.error("Driver, file and reason are required");
    setBusy(true);
    const fd = new FormData(); Object.entries(form).forEach(([k,v]) => v && fd.append(k,v));
    try { await api.post("/penalties/with-evidence", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Evidence case created in Pending Review"); setOpen(false); setForm({ driver_id:"",penalty_type:"signature_dispute",reason:"",incident_date:"",file:null }); onCreated(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return <Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild><Button variant="outline"><Camera className="w-4 h-4 mr-1.5"/>Upload evidence</Button></DialogTrigger><DialogContent><DialogHeader><DialogTitle>New evidence case</DialogTitle><DialogDescription>Upload from Camera, Gallery or a saved WhatsApp image. The case stays Pending Review.</DialogDescription></DialogHeader><form onSubmit={submit} className="space-y-3"><div><Label>Driver</Label><Select value={form.driver_id} onValueChange={(v)=>setForm({...form,driver_id:v})}><SelectTrigger><SelectValue placeholder="Select driver"/></SelectTrigger><SelectContent>{(drivers||[]).map((d)=><SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>)}</SelectContent></Select></div><div><Label>Case type</Label><Select value={form.penalty_type} onValueChange={(v)=>setForm({...form,penalty_type:v})}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="signature_dispute">Signature dispute</SelectItem><SelectItem value="damage_issue">Damage issue</SelectItem><SelectItem value="customer_complaint">Customer / parcel issue</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div><div><Label>Photo or PDF</Label><Input type="file" accept="image/*,application/pdf" capture="environment" onChange={(e)=>setForm({...form,file:e.target.files?.[0]||null})}/></div><div><Label>Reason</Label><Textarea value={form.reason} onChange={(e)=>setForm({...form,reason:e.target.value})}/></div><div><Label>Incident date</Label><Input type="date" value={form.incident_date} onChange={(e)=>setForm({...form,incident_date:e.target.value})}/></div><DialogFooter><Button type="submit" disabled={busy}>{busy?"Uploading…":"Create Pending Review"}</Button></DialogFooter></form></DialogContent></Dialog>;
};

const CreatePenaltyDialog = ({ drivers, types, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    driver_id: "", penalty_type: "damage", amount: "", reason: "", incident_date: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.driver_id || !form.reason.trim() || Number(form.amount) <= 0) {
      toast.error("Driver, reason and a positive amount are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/penalties", {
        driver_id: form.driver_id,
        penalty_type: form.penalty_type,
        amount: Number(form.amount),
        reason: form.reason.trim(),
        incident_date: form.incident_date || null,
      });
      toast.success("Penalty created — awaiting review. Payroll is NOT affected until you APPLY it.");
      setOpen(false);
      setForm({ driver_id: "", penalty_type: "damage", amount: "", reason: "", incident_date: "" });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand-red hover:bg-brand-red-hover" data-testid="penalty-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New penalty
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="penalty-create-dialog">
        <DialogHeader>
          <DialogTitle>Raise a new penalty</DialogTitle>
          <DialogDescription>
            Creates a penalty in <b>Pending Review</b>. The driver will see it and can submit a
            dispute with signature evidence. <b>No payroll deduction is created</b> until you
            explicitly click <b>Apply</b> after approval.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Driver</Label>
            <Select value={form.driver_id} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
              <SelectTrigger data-testid="penalty-driver-select" className="mt-1.5"><SelectValue placeholder="Select a driver" /></SelectTrigger>
              <SelectContent>
                {(drivers || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.driver_code} — {d.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Type</Label>
              <Select value={form.penalty_type} onValueChange={(v) => setForm({ ...form, penalty_type: v })}>
                <SelectTrigger data-testid="penalty-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(types || []).map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Amount (£)</Label>
              <Input type="number" step="0.01" min="0.01" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     data-testid="penalty-amount" className="mt-1.5 font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Incident date</Label>
            <Input type="date" value={form.incident_date}
                   onChange={(e) => setForm({ ...form, incident_date: e.target.value })}
                   data-testid="penalty-date" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Reason / evidence summary</Label>
            <Textarea rows={3} value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      data-testid="penalty-reason" className="mt-1.5"
                      placeholder="What happened, where, photo audit ID, contract ref…" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}
                    className="bg-brand-red hover:bg-brand-red-hover"
                    data-testid="penalty-save">
              {busy ? "Saving…" : "Create in Pending Review"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PenaltyRow = ({ p, drivers, onChanged, canManage }) => {
  const [busyAction, setBusyAction] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [note, setNote] = useState("");
  const [edit, setEdit] = useState({ amount: p.amount || "", reason: p.reason || "", penalty_type: p.penalty_type || "other", incident_date: p.incident_date || "" });
  const driver = (drivers || []).find((d) => d.id === p.driver_id);

  const doAction = async (path, body = {}) => {
    setBusyAction(path);
    try {
      const { data } = await api.post(`/penalties/${p.id}/${path}`, body);
      toast.success(`Penalty ${data.state}`);
      setNote("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setBusyAction(null);
  };

  const canApprove = canManage && (p.state === "pending_review" || p.state === "disputed");
  const canReject  = canApprove;
  const canApply   = canManage && p.state === "approved";
  const canCancel  = canManage && (p.state === "pending_review" || p.state === "disputed" || p.state === "approved");
  const canEdit = canManage && !["applied", "rejected", "cancelled"].includes(p.state);
  const saveEdit = async () => {
    const payload = { ...edit };
    if (edit.amount === "" || edit.amount == null) delete payload.amount; else payload.amount = Number(edit.amount);
    if (!payload.incident_date) delete payload.incident_date;
    try { await api.patch(`/penalties/${p.id}`, payload); toast.success("Penalty updated"); onChanged(); }
    catch(e){toast.error(e?.response?.data?.detail || "Edit failed");}
  };

  return (
    <>
      <tr data-testid={`penalty-row-${p.id}`}
          className="hover:bg-brand-surface/50 cursor-pointer"
          onClick={() => setDetailOpen(true)}>
        <td className="px-4 py-2.5 text-xs font-mono">{ddmmyyyy(p.created_at?.slice(0, 10))}</td>
        <td className="px-4 py-2.5">
          <div className="text-xs font-medium">{driver?.full_name || "—"}</div>
          <div className="text-[10px] font-mono text-brand-text-muted">{driver?.driver_code}</div>
        </td>
        <td className="px-4 py-2.5 text-xs">{(p.penalty_type || "").replace(/_/g, " ")}</td>
        <td className="px-4 py-2.5 text-xs max-w-md truncate" title={p.reason}>{p.reason}</td>
        <td className="px-4 py-2.5 text-right font-mono text-xs">
          {p.amount_hidden ? "—" : gbp(p.amount)}
        </td>
        <td className="px-4 py-2.5"><StateBadge state={p.state} /></td>
      </tr>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg" data-testid={`penalty-detail-${p.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-brand-red" />
              Penalty #{p.id.slice(0, 8)}
            </DialogTitle>
            <DialogDescription>
              {(p.penalty_type || "").replace(/_/g, " ")} · {gbp(p.amount)} · driver {driver?.full_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="rounded-lg border border-brand-border p-3 bg-brand-surface">
              <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Reason</div>
              <div className="mt-1 whitespace-pre-wrap">{p.reason}</div>
              {p.incident_date && (
                <div className="mt-2 text-xs text-brand-text-muted">Incident: {ddmmyyyy(p.incident_date)}</div>
              )}
            </div>

            {p.driver_dispute && (
              <div className="rounded-lg border border-purple-200 bg-purple-50 p-3"
                   data-testid={`dispute-block-${p.id}`}>
                <div className="flex items-center gap-1.5 text-purple-800 font-semibold text-xs uppercase tracking-wider">
                  <FileSignature className="w-3.5 h-3.5" /> Driver dispute
                </div>
                <div className="mt-1 text-xs whitespace-pre-wrap text-purple-900">{p.driver_dispute.reason}</div>
                <div className="mt-2 text-[10px] text-purple-700 font-mono">
                  Submitted {p.driver_dispute.submitted_at?.slice(0, 19).replace("T", " ")} UTC
                  {p.driver_dispute.signature_captured_ip ? ` · IP ${p.driver_dispute.signature_captured_ip}` : ""}
                </div>
                {p.driver_dispute.signature_png_b64 && (
                  <img src={p.driver_dispute.signature_png_b64}
                       alt="Driver signature"
                       className="mt-2 max-h-24 bg-white rounded border border-purple-200 p-1"
                       data-testid={`dispute-signature-${p.id}`} />
                )}
              </div>
            )}

            {p.admin_decision && (
              <div className="rounded-lg border border-brand-border p-3">
                <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">
                  Admin decision · {p.admin_decision.action}
                </div>
                <div className="mt-1 text-xs">
                  by {p.admin_decision.by_name} · {p.admin_decision.at?.slice(0, 19).replace("T", " ")} UTC
                </div>
                {p.admin_decision.note && (
                  <div className="mt-1 text-xs italic">&ldquo;{p.admin_decision.note}&rdquo;</div>
                )}
              </div>
            )}

            {p.state === "applied" && p.applied_deduction_id && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                <b>Payroll linked.</b> Deduction ID{" "}
                <span className="font-mono">{p.applied_deduction_id.slice(0, 8)}</span>{" "}
                created in the pay period. To reverse, delete from the Deductions page.
              </div>
            )}

            {(p.evidence || []).length > 0 && <div className="rounded-lg border border-brand-border p-3"><div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Evidence</div><div className="flex flex-wrap gap-2 mt-2">{p.evidence.map((e)=><a key={e.id || e.ref} href={e.id ? `${API_BASE}/penalties/${p.id}/evidence/${e.id}` : e.url || "#"} target="_blank" rel="noreferrer" className="text-xs underline text-brand-navy">{e.label || e.kind || "Open evidence"}</a>)}</div></div>}

            {canEdit && <div className="rounded-lg border border-brand-border p-3 space-y-2"><div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Edit before approval</div><div className="grid grid-cols-2 gap-2"><Input type="number" step="0.01" placeholder="Amount (£)" value={edit.amount} onChange={(e)=>setEdit({...edit,amount:e.target.value})}/><Input type="date" value={edit.incident_date} onChange={(e)=>setEdit({...edit,incident_date:e.target.value})}/></div><Textarea value={edit.reason} onChange={(e)=>setEdit({...edit,reason:e.target.value})}/><Button size="sm" variant="outline" onClick={saveEdit}><Pencil className="w-3.5 h-3.5 mr-1"/>Save changes</Button></div>}

            {(canApprove || canReject || canApply) && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Note (optional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="Why are you approving / rejecting / applying?"
                          data-testid={`penalty-note-${p.id}`} className="mt-1.5" />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 flex-wrap">
            {canApprove && (
              <Button onClick={() => doAction("approve", { note })}
                       disabled={busyAction === "approve"}
                       className="bg-blue-600 hover:bg-blue-700 text-white"
                       data-testid={`penalty-approve-${p.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve
              </Button>
            )}
            {canReject && (
              <Button variant="outline" onClick={() => doAction("reject", { note })}
                       disabled={busyAction === "reject"}
                       className="border-gray-400 text-gray-700"
                       data-testid={`penalty-reject-${p.id}`}>
                <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
              </Button>
            )}
            {canApply && (
              <Button onClick={() => doAction("apply", { note })}
                       disabled={busyAction === "apply"}
                       className="bg-emerald-600 hover:bg-emerald-700 text-white"
                       data-testid={`penalty-apply-${p.id}`}>
                <Send className="w-3.5 h-3.5 mr-1.5" /> Apply to payroll
              </Button>
            )}
            {canCancel && (
              <Button variant="ghost" onClick={() => doAction("cancel", { note })}
                       disabled={busyAction === "cancel"}
                       className="text-red-600"
                       data-testid={`penalty-cancel-${p.id}`}>
                <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancel
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Penalties = () => {
  const { user } = useAuth();
  const { data: penalties, mutate: reload } = useSWR("/penalties", fetcher);
  const { data: drivers } = useSWR("/drivers", fetcher);
  const { data: types } = useSWR("/penalties/types", fetcher);
  const [filterState, setFilterState] = useState("all");

  const canManage = user?.role === "platform_owner" || user?.role === "company_admin";

  const visible = (penalties || []).filter((p) => filterState === "all" || p.state === filterState);

  const counts = (penalties || []).reduce((acc, p) => {
    acc[p.state] = (acc[p.state] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Payroll · Penalties</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Penalties &amp; Disputes</h1>
          <p className="text-sm text-brand-text-muted mt-1 max-w-2xl">
            Raise penalties (damage, uniform, fuel misuse&hellip;) and route them through
            <b> Pending Review → Approved → Applied</b>. Drivers can dispute with a signed statement.
            <b> Nothing hits payroll until you press Apply.</b>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap"><EvidenceCaseDialog drivers={drivers} onCreated={reload} />{canManage && <CreatePenaltyDialog drivers={drivers} types={types} onCreated={reload} />}</div>
      </div>
      {/* Bucket filters */}
      <div className="flex flex-wrap gap-2" data-testid="penalty-filter-bar">
        {["all", "pending_review", "disputed", "approved", "applied", "rejected", "cancelled"].map((s) => (
          <button key={s} onClick={() => setFilterState(s)}
                  data-testid={`penalty-filter-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${filterState === s
                    ? "bg-brand-navy text-white border-brand-navy"
                    : "bg-white text-brand-text border-brand-border hover:bg-brand-surface"}`}>
            {s === "all" ? "All" : (STATE_META[s]?.label || s)}
            {s !== "all" && counts[s] ? ` · ${counts[s]}` : ""}
          </button>
        ))}
      </div>

      <Card className="rounded-2xl border-brand-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="penalties-table">
            <thead className="bg-brand-surface text-brand-text-muted uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Raised</th>
                <th className="text-left px-4 py-3">Driver</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">State</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {visible.map((p) => (
                <PenaltyRow key={p.id} p={p} drivers={drivers} onChanged={reload} canManage={canManage} />
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-brand-text-muted text-sm">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2 opacity-40" />
                    No penalties {filterState === "all" ? "raised yet" : `in ${STATE_META[filterState]?.label || filterState}`}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-[11px] text-brand-text-muted flex items-center gap-1.5">
        <HistoryIcon className="w-3 h-3" />
        Every state change is recorded in the penalty audit log with actor, IP and signature evidence.
      </div>
    </div>
  );
};

export default Penalties;
